import React from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  View,
  type ViewStyle,
} from 'react-native';

import { GlassButton } from '../ui/GlassButton';
import { GlassCard } from '../ui/GlassCard';
import { RefreshButton } from '../ui/RefreshButton';
import { colors, spacing, typography } from '../../core/theme/tokens';
import { formatCurrency } from '../charts/d3/format';

type NetWorthStripProps = {
  activeInvestments: number;
  passiveInvestments: number;
  totalLiabilities: number;
  total: number;
  onPress?: () => void;
  /** Opens import statement / screenshot sheet (Active investments cell). */
  onActiveInvestmentsPress?: () => void;
  /** Opens manual Add account sheet (Other assets cell). */
  onOtherAssetsPress?: () => void;
  actionLabel?: string;
};

export function NetWorthStrip({
  activeInvestments,
  passiveInvestments,
  totalLiabilities,
  total,
  onPress,
  onActiveInvestmentsPress,
  onOtherAssetsPress,
  actionLabel = 'Wealth',
}: NetWorthStripProps) {
  const cells = [
    {
      id: 'active',
      label: 'Active investments',
      value: activeInvestments,
      tone: 'accent' as const,
      onPress: onActiveInvestmentsPress,
      a11y: 'Open import',
    },
    {
      id: 'other',
      label: 'Other assets',
      value: passiveInvestments,
      tone: 'default' as const,
      onPress: onOtherAssetsPress,
      a11y: 'Add other asset',
    },
    {
      id: 'liabilities',
      label: 'Liabilities',
      value: totalLiabilities,
      tone: 'warning' as const,
    },
    {
      id: 'net',
      label: 'Net worth',
      value: total,
      tone: 'accent' as const,
      emphasize: true,
    },
  ];

  // Do not put onPress on GlassCard when children include buttons — nested
  // <button> breaks web DOM and causes layout/press jank.
  return (
    <GlassCard style={styles.card}>
      <View style={styles.header}>
        <View style={styles.headerText}>
          <Text style={styles.title}>Net worth</Text>
          <Text style={styles.hint}>Assets minus liabilities — home loan included</Text>
        </View>
        <View style={styles.headerActions}>
          <RefreshButton compact />
          {onPress && (
            <GlassButton
              variant="ghost"
              size="sm"
              onPress={onPress}
              accessibilityLabel="Open wealth tab"
            >
              {actionLabel}
            </GlassButton>
          )}
        </View>
      </View>
      <View style={styles.row}>
        {cells.map((cell) => {
          const content = (
            <>
              <Text style={styles.cellLabel}>{cell.label}</Text>
              <Text
                style={[
                  styles.cellValue,
                  cell.tone === 'accent' && styles.valueAccent,
                  cell.tone === 'warning' && styles.valueWarning,
                ]}
              >
                {cell.tone === 'warning'
                  ? `−${formatCurrency(cell.value)}`
                  : formatCurrency(cell.value)}
              </Text>
            </>
          );

          if (cell.onPress) {
            return (
              <Pressable
                key={cell.id}
                onPress={() => cell.onPress?.()}
                style={({ pressed }) => [
                  styles.cell,
                  cell.emphasize && styles.cellEmphasize,
                  styles.cellPressable,
                  pressed && styles.cellPressed,
                ]}
                accessibilityRole="button"
                accessibilityLabel={`${cell.label}. ${cell.a11y ?? 'Open'}`}
              >
                {content}
              </Pressable>
            );
          }

          return (
            <View
              key={cell.id}
              style={[styles.cell, cell.emphasize && styles.cellEmphasize]}
            >
              {content}
            </View>
          );
        })}
      </View>
    </GlassCard>
  );
}

const styles = StyleSheet.create({
  card: {
    gap: spacing.sm,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  headerText: {
    flex: 1,
    gap: 2,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  title: {
    ...typography.headline,
    color: colors.text,
  },
  hint: {
    ...typography.caption,
    color: colors.textMuted,
  },
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  cell: {
    flexGrow: 1,
    flexBasis: '45%',
    minWidth: 120,
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    gap: 4,
  },
  cellPressable: {
    borderColor: 'rgba(61, 214, 164, 0.28)',
    cursor: 'pointer',
  } as ViewStyle,
  cellPressed: {
    opacity: 0.85,
  },
  cellEmphasize: {
    borderColor: 'rgba(61, 214, 164, 0.35)',
    backgroundColor: 'rgba(61, 214, 164, 0.08)',
  },
  cellLabel: {
    ...typography.label,
    color: colors.textMuted,
    textTransform: 'uppercase',
  },
  cellValue: {
    ...typography.headline,
    color: colors.text,
    fontSize: 18,
    fontVariant: ['tabular-nums'],
  },
  valueAccent: {
    color: colors.accent,
  },
  valueWarning: {
    color: colors.warning,
  },
});
